import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getToken, setToken, clearToken } from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState("");

  const retryAuth = useCallback(async () => {
    const t = getToken();
    if (!t) { setLoading(false); setUser(null); setAuthError(""); return; }
    setLoading(true);
    try {
      const response = await api.get("/api/auth/me");
      setUser(response.data.user);
      setAuthError("");
    } catch (error) {
      if ([401, 403].includes(error?.response?.status)) {
        clearToken(); setUser(null); setAuthError("");
      } else {
        setAuthError("The CRM authentication service is temporarily unavailable. Your saved session has not been removed.");
      }
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { retryAuth(); }, [retryAuth]);

  const login = async (password) => {
    const r = await api.post("/api/auth/login", { password });
    setToken(r.data.token);
    setUser(r.data.user);
    return r.data;
  };

  const logout = () => { clearToken(); setUser(null); window.location.href = "/login"; };

  const loginWithToken = useCallback(async (token) => {
    setToken(token);
    try {
      const response = await api.get("/api/auth/me");
      if (!response.data?.user) throw new Error("Invalid admin session");
      setUser(response.data.user);
      setAuthError("");
      return response.data;
    } catch (error) {
      if ([401, 403].includes(error?.response?.status)) clearToken();
      else setAuthError("The CRM authentication service is temporarily unavailable. Retry when the service is ready.");
      setUser(null);
      throw error;
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithToken, logout, authError, retryAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
